/**
 * 1.8.5 §9 (D-1.8.5-9) — the Internet check: "is this device really online, and how fast is it here?"
 *
 *   1. Online? — a small request to the app's own API (GET /api/net/ping) with a timeout. Never the browser's own
 *      `navigator.onLine`, which says "online" whenever the Wi-Fi is joined, internet or not.
 *   2. How fast? — a timed download of a fixed, incompressible 16 MiB file from the app's own site
 *      (/net-check/speed-16mb.bin, made at build time by scripts/gen-net-check.mjs), caching off, said in words.
 *      Private: no third party sees the test.
 *
 * Pure: the fetch and the clock are passed in, so the app and the tests share it. The words live in the i18n files
 * (net185.*); this module gives the band, the reason and the numbers.
 */
import { serviceDateKey } from './dates';
import { steadyTraffic, downloadTransfer, TrafficFailure } from './net-measure';

/** The speed file: 16 MiB of seeded pseudo-random bytes (incompressible), served by the app's own site. */
export const SPEED_FILE_PATH = '/net-check/speed-16mb.bin';
export const SPEED_FILE_BYTES = 16 * 1024 * 1024;
export const PING_PATH = '/api/net/ping';
export const COUNT_PATH = '/api/net/count';
export const TESTS_PATH = '/api/net/tests';

/** The ping has 5 seconds to answer; the download is stopped after 20 (and measured from what arrived). */
export const PING_TIMEOUT_MS = 5_000;
export const SPEED_CAP_MS = 20_000;

/**
 * The bands (megabits a second, measured on this device over this Wi-Fi):
 *   - 10 and up: "Good for video calls" — headroom over the FCC's household guide figure for HD video calls (6 Mbps),
 *     because one download over one connection reads a little lower than the line really is;
 *   - 2 up to 10: "Fine for email and browsing";
 *   - under 2: "Slow".
 */
export const VIDEO_MBPS = 10;
export const BROWSE_MBPS = 2;
export type SpeedBand = 'good' | 'fine' | 'slow';

export function speedBand(mbps: number): SpeedBand {
  if (!Number.isFinite(mbps) || mbps < BROWSE_MBPS) return 'slow';
  return mbps >= VIDEO_MBPS ? 'good' : 'fine';
}

/** Megabits a second from bytes and milliseconds (one decimal). */
export function mbpsOf(bytes: number, ms: number): number {
  if (bytes <= 0 || ms <= 0) return 0;
  return Math.round(((bytes * 8) / (ms / 1000) / 1_000_000) * 10) / 10;
}

/** RequestInit with `cache` (the DOM has it; Node's types, which the API compiles this with, do not). */
type FetchInit = Omit<RequestInit, 'cache'> & { cache?: 'no-store' };
type FetchLike = (input: string, init?: FetchInit) => Promise<Response>;

export type OnlineResult = { state: 'online'; ms: number } | { state: 'offline'; why: 'timeout' | 'network' | 'answer' };

/**
 * Online? The app's own API answers `{ pong: true }`. Anything else — no answer in time, a network error, or an
 * answer that is not the app's (a Wi-Fi sign-in page that catches every request) — is "No internet".
 */
export async function checkOnline(fetchImpl: FetchLike, o: { timeoutMs?: number; now?: () => number; url?: string } = {}): Promise<OnlineResult> {
  const now = o.now ?? (() => Date.now());
  const ctrl = new AbortController();
  const t0 = now();
  const timer = setTimeout(() => ctrl.abort(), o.timeoutMs ?? PING_TIMEOUT_MS);
  try {
    const res = await fetchImpl(`${o.url ?? PING_PATH}?n=${t0.toString(36)}`, { cache: 'no-store', credentials: 'same-origin', signal: ctrl.signal, headers: { Accept: 'application/json' } });
    const type = res.headers.get('content-type') ?? '';
    const body = res.ok && type.includes('application/json') ? ((await res.json().catch(() => null)) as { pong?: unknown } | null) : null;
    if (!body || body.pong !== true) return { state: 'offline', why: 'answer' };
    return { state: 'online', ms: Math.max(0, now() - t0) };
  } catch {
    return { state: 'offline', why: ctrl.signal.aborted ? 'timeout' : 'network' };
  } finally {
    clearTimeout(timer);
  }
}

export type SpeedResult =
  | { ok: true; bytes: number; ms: number; mbps: number; band: SpeedBand; capped: boolean; preliminary?: boolean }
  | { ok: false; why: 'timeout' | 'network' | 'http' | 'missing'; bytes?: number };

/** Under this many bytes, a finished download is not the speed file (a fallback page answered instead). */
const MIN_FILE_BYTES = 1024 * 1024;

/**
 * How fast? Downloads the speed file once (a fresh query each time, `cache: 'no-store'`), counting the bytes as they
 * arrive. After `capMs` the download is stopped and measured from what arrived (`capped`). The clock runs from the
 * request to the last byte, so it is a little conservative.
 */
export async function measureStartupSpeed(fetchImpl: FetchLike, o: { capMs?: number; now?: () => number; url?: string; nonce?: string } = {}): Promise<SpeedResult> {
  const now = o.now ?? (() => Date.now());
  const ctrl = new AbortController();
  const t0 = now();
  const timer = setTimeout(() => ctrl.abort(), o.capMs ?? SPEED_CAP_MS);
  const url = `${o.url ?? SPEED_FILE_PATH}?n=${o.nonce ?? `${t0.toString(36)}${Math.random().toString(36).slice(2, 8)}`}`;
  let bytes = 0;
  let capped = false;
  try {
    let res: Response;
    try {
      res = await fetchImpl(url, { cache: 'no-store', credentials: 'same-origin', signal: ctrl.signal });
    } catch {
      return { ok: false, why: ctrl.signal.aborted ? 'timeout' : 'network' };
    }
    if (!res.ok) return { ok: false, why: 'http' };
    if ((res.headers.get('content-type') ?? '').includes('text/html')) return { ok: false, why: 'missing' };
    const reader = res.body?.getReader?.();
    if (reader) {
      // at the cap the read in progress is cancelled too (a body not tied to the request's signal would wait on)
      const stop = () => void reader.cancel().catch(() => undefined);
      ctrl.signal.addEventListener('abort', stop);
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          bytes += value?.byteLength ?? 0;
        }
        if (ctrl.signal.aborted) capped = true;
      } catch {
        if (!ctrl.signal.aborted) return { ok: false, why: 'network' };
        capped = true;
      } finally {
        ctrl.signal.removeEventListener('abort', stop);
      }
    } else {
      try {
        bytes = (await res.arrayBuffer()).byteLength;
      } catch {
        if (!ctrl.signal.aborted) return { ok: false, why: 'network' };
        capped = true;
      }
    }
    const ms = Math.max(1, now() - t0);
    if (bytes === 0) return { ok: false, why: capped ? 'timeout' : 'network' };
    if (!capped && bytes < MIN_FILE_BYTES) return { ok: false, why: 'missing' };
    const mbps = mbpsOf(bytes, ms);
    return { ok: true, bytes, ms, mbps, band: speedBand(mbps), capped };
  } finally {
    clearTimeout(timer);
  }
}

/** 1.8.9: six concurrent transfers, warm-up excluded; quick by default, bounded full test. */
export async function measureSpeed(fetchImpl: FetchLike, o: { full?: boolean; capMs?: number; now?: () => number; url?: string; nonce?: string; signal?: AbortSignal } = {}): Promise<SpeedResult> {
  try {
    const r = await steadyTraffic(downloadTransfer(fetchImpl, o.url ?? SPEED_FILE_PATH, o.nonce), { streams: 6, durationMs: o.capMs ?? (o.full ? 8000 : 3000), byteLimit: (o.full ? 200 : 96) * 1024 * 1024, now: o.now, signal: o.signal });
    return { ok: true, ...r, band: speedBand(r.mbps) };
  } catch (e) {
    if (o.signal?.aborted) throw e;
    const why = e instanceof Error && ['timeout', 'http', 'missing'].includes(e.message) ? e.message as 'timeout' | 'http' | 'missing' : 'network';
    return { ok: false, why, bytes: e instanceof TrafficFailure ? e.bytes : 0 };
  }
}

/* ---------------- the building's service: the providers' own tests ---------------- */

export interface ProviderTest {
  id: 'spectrum' | 'verizon';
  name: string;
  url: string;
  /** The provider's disability support line in TECH_LIBRARY.freeHelp. */
  freeHelpId: string;
}

/** The building's two providers (catalog §10, §13): their own tests, linked. (The speed test itself runs on M-Lab since D-1024-TECHHELPER.) */
export const PROVIDER_TESTS: readonly ProviderTest[] = [
  { id: 'spectrum', name: 'Spectrum', url: 'https://www.spectrum.com/internet/speed-test', freeHelpId: 'spectrum-disability' },
  { id: 'verizon', name: 'Verizon Fios', url: 'https://www.verizon.com/speedtest/', freeHelpId: 'verizon-disabilities' },
];

/* ---------------- the month's count, for Health ---------------- */

/** Static Web Apps Standard includes 100 GB of bandwidth a month (about 6,000 tests at 16 MiB). */
export const INCLUDED_GB = 100;

/** `_app/net-check/<yyyyMM>.json` — counts only: no who, no where, no result. */
export const NET_COUNT_FOLDER = '_app/net-check';
export interface NetCountFile {
  month: string;
  tests: number;
  updatedAt: string;
  version?: number;
  bytes?: number;
  /** Tests measured on M-Lab (D-1024-TECHHELPER): counted as tests, 0 bytes through the app. */
  mlabTests?: number;
}

export interface NetTestsResponse {
  month: string;
  tests: number;
  /** About how many GB the month's tests downloaded (tests × 16 MiB). */
  gb: number;
  includedGb: number;
  bytes?: number;
  /** How many of the month's tests ran on M-Lab (no cost to the app's bandwidth). */
  mlabTests?: number;
}

/** The month (yyyyMM, America/New_York) of an instant. */
export function netMonth(now: Date = new Date()): string {
  return serviceDateKey(now).slice(0, 6);
}

export function gbOf(tests: number): number {
  return Math.round(((tests * SPEED_FILE_BYTES) / 1_000_000_000) * 10) / 10;
}
